import {
  type FC, type ReactNode, useCallback, useLayoutEffect, useMemo, useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type AssistantRuntime,
  AssistantRuntimeProvider,
  type RemoteThreadListAdapter,
  type ThreadRuntime,
  useRemoteThreadListRuntime,
} from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { createAssistantStream } from 'assistant-stream';

import { useAndiAPI } from '../api/andiClient';
import type { Chat } from '../api/types';
import { andiChatPath, UNTITLED_CHAT } from '../constants';
import { chatQueryKey, getChatQueryObject } from '../hooks/useChatQuery';
import { useLatest } from '../hooks/useLatest';
import { useOnAndiPage } from '../hooks/useOnAndiPage';

import { useChatThreadRuntime } from './useChatThreadRuntime';

/** How long creating a chat waits for its opening message to land in the thread. */
const OPENING_MESSAGE_WAIT_MS = 1_500;

/** The text of a thread's first message, once it lands ('' if it doesn't within `ms`). */
function firstMessageText(thread: ThreadRuntime, ms: number): Promise<string> {
  const read = () => {
    const first = thread.getState().messages.find((message) => message.role === 'user');
    return first?.content.map((part) => (part.type === 'text' ? part.text : '')).join('').trim() ?? '';
  };
  const now = read();
  if (now) return Promise.resolve(now);
  return new Promise((resolve) => {
    const finish = (text: string) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(text);
    };
    const unsubscribe = thread.subscribe(() => {
      const text = read();
      if (text) finish(text);
    });
    const timer = setTimeout(() => finish(''), ms);
  });
}

const listItemOf = (chatId: string, chat: Pick<Chat, 'title' | 'headline' | 'provider' | 'status'>, fallback = '') => ({
  status: 'regular' as const,
  remoteId: chatId,
  title: chat.title || chat.headline || fallback || UNTITLED_CHAT,
  custom: { provider: chat.provider },
});

type AndiRuntimeProviderProps = { children: ReactNode };

/**
 * One assistant-ui runtime for Andi's pages: the chat list (rendered in the sidebar) and, for each chat, the
 * runtime from useChatThreadRuntime. The URL decides which chat is open.
 */
const AndiRuntime: FC<AndiRuntimeProviderProps> = ({ children }) => {
  const { projectId, chatId } = useParams();
  const navigate = useNavigate();
  const getAPI = useAndiAPI();
  const getAPIRef = useLatest(getAPI);
  const navigateRef = useLatest(navigate);
  const openChatIdRef = useLatest(chatId);
  const projectIdRef = useLatest(projectId);
  const queryClient = useQueryClient();
  // Set once the runtime exists; read only when a chat is created.
  const runtimeRef = useRef<AssistantRuntime | null>(null);

  // Stable, reading everything through refs, so the runtime never reloads the list because of it.
  const adapter = useMemo<RemoteThreadListAdapter>(() => {
    const chatQuery = (id: string) => getChatQueryObject(projectIdRef.current, id, getAPIRef.current);
    return {
      list: async () => {
        const chats = await (await getAPIRef.current()).listChats();
        return {
          threads: chats.map((summary) => ({
            ...listItemOf(summary.id, summary, summary.last_message),
            lastMessageAt: summary.started_at ? new Date(summary.started_at) : undefined,
          })),
        };
      },
      rename: async (id, title) => {
        await (await getAPIRef.current()).renameChat(id, title);
      },
      delete: async (id) => {
        await (await getAPIRef.current()).deleteChat(id);
        queryClient.removeQueries({ queryKey: chatQueryKey(projectIdRef.current, id) });
        if (id === openChatIdRef.current) navigateRef.current(andiChatPath(projectIdRef.current));
      },
      // A chat is created with its opening message, so it's never blank and the server can name it.
      initialize: async (threadId) => {
        let opening = '';
        try {
          const thread = runtimeRef.current?.threads.getById(threadId);
          if (thread) opening = await firstMessageText(thread, OPENING_MESSAGE_WAIT_MS);
        } catch {
          // No opening text: the chat is still created, and the first reply carries the message.
        }
        const { id } = await (await getAPIRef.current()).startChat(opening);
        void queryClient.prefetchQuery(chatQuery(id));
        return { remoteId: id };
      },
      archive: async () => {},
      unarchive: async () => {},
      // The server names the chat; hand that name to assistant-ui instead of reloading the whole list.
      generateTitle: async (id) => createAssistantStream(async (controller) => {
        const chat = await queryClient.fetchQuery({ ...chatQuery(id), staleTime: 0 }).catch(() => null);
        const title = chat?.title || chat?.headline;
        if (title) controller.appendText(title);
      }),
      // The open chat, before the list has loaded: named as the list will name it, so its row doesn't change.
      fetch: async (id) => listItemOf(id, await queryClient.fetchQuery(chatQuery(id))),
    };
  }, [getAPIRef, navigateRef, openChatIdRef, projectIdRef, queryClient]);

  const onThreadIdChange = useCallback((id: string | undefined) => {
    navigate(andiChatPath(projectId, id));
  }, [navigate, projectId]);

  // assistant-ui runs each chat's runtime hook in a host of its own, where the route's params can still be the
  // previous project's after a switch: a new chat's first message went to the project just left, which answered
  // "not found". This runtime is keyed by the project, so it hands its own to every chat's runtime.
  const runtimeHook = useMemo(() => function useProjectChatRuntime() {
    return useChatThreadRuntime(projectId);
  }, [projectId]);

  const runtime = useRemoteThreadListRuntime({
    runtimeHook,
    adapter,
    threadId: chatId,
    onThreadIdChange,
  });
  useLayoutEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
};

/** Gives the layout Andi's runtime on Andi's pages, so the sidebar's chat list and the open chat share it.
 *  Anywhere else it renders its children as they are. Each project gets its own runtime: the runtime reads
 *  the project through refs, so one kept across a project switch could load a chat under the project just
 *  left, which the server answers with "not found". */
export const AndiRuntimeProvider: FC<AndiRuntimeProviderProps> = ({ children }) => {
  const { projectId } = useParams();
  return useOnAndiPage() ? <AndiRuntime key={projectId}>{children}</AndiRuntime> : children;
};
